/**
 * 商店系统：谁家卖什么、什么时候开门、今天什么价。
 *
 * 铺子的位置与时辰在世界层（data/world.js）里判，这里只管柜台后面的事：
 *   SHOPS   — 十三家的本子（库存、收货、开口封口）
 *   priceOf — 今日实际价 = 底价 x 行情 x 你看起来像不像个正经人
 *   buy/sell— 一手交钱一手交货，落在 db 上
 */
import { Repo } from '../data/schema.js';
import { Avatar } from './avatar.js';
import { Tags } from './tags.js';
import { Inspire } from './inspire.js';
import { getItem } from '../data/items.js';

/** 每四刻钟（游戏分钟）一个行情段落，一天之内价会慢慢走 */
function daySlot(world) {
  return Math.floor((world.minute || 0) / 60);
}

/** 每日 + 每段的行情浮动 */
function marketFactor(world, shopId, itemId) {
  const day = world.day || 1;
  const slot = daySlot(world);
  let h = day * 131 + slot * 17;
  for (let i = 0; i < shopId.length; i += 1) h = (h * 31 + shopId.charCodeAt(i)) >>> 0;
  for (let i = 0; i < itemId.length; i += 1) h = (h * 33 + itemId.charCodeAt(i)) >>> 0;
  const r = ((h % 1000) / 1000); // 0~1
  return 0.85 + r * 0.35;        // 0.85 ~ 1.20
}

/**
 * 十三家的本子。
 *  hours  [开门分钟, 关门分钟]，null 表示全天
 *  sells  常备货：{ id, price, stock }
 *  buys   收货：物品id -> 收货底价（乘行情）
 *  services 特殊服务（睡/泡澡/写字），由 UI 决定怎么问
 *  forbids 不肯做什么脏活：没有对应证照就不给 tradePurple
 */
export const SHOPS = {
  garage: {
    id: 'garage', name: '车铺', owner: '郭大有', scene: 'garage',
    hours: [7 * 60, 19 * 60], kind: 'garage',
    sells: [
      { id: 'bolt', price: 4, stock: 20 }, { id: 'usedTire', price: 12, stock: 6 },
      { id: 'wasteEngineOil', price: 6, stock: 10 }, { id: 'steelWireScrap', price: 3, stock: 20 },
      { id: 'usedValve', price: 5, stock: 12 }, { id: 'usedDiff', price: 40, stock: 2 },
      // 攒车的大件：拾荒也能翻出来，这儿是拿钱换时间的门路，都是死贵还限量的
      { id: 'scrap_iron', price: 4, stock: 30 }, { id: 'plate_scrap', price: 7, stock: 20 },
      { id: 'wood_scrap', price: 5, stock: 20 }, { id: 'tire', price: 22, stock: 4 },
      { id: 'frame', price: 90, stock: 1 }, { id: 'seat_part', price: 35, stock: 2 },
      { id: 'battery', price: 60, stock: 1 }, { id: 'engine', price: 180, stock: 1 },
      { id: 'fuel', price: 14, stock: 6 },
    ],
    buys: { scrap_iron: 2, bolt: 2, usedTire: 6, engine: 60, usedDiff: 24, oldSteeringWheel: 8, usedValve: 3, usedOilPump: 14 },
    services: ['assemble'], says: '郭大有从车底下滑出来，抹了把脸上的油。',
  },
  eatery: {
    id: 'eatery', name: '早点铺', owner: '面婶', scene: 'eatery',
    hours: [5 * 60, 14 * 60], kind: 'eatery',
    sells: [
      { id: 'meal', price: 8, stock: 10 }, { id: 'rice', price: 4, stock: 30 },
      { id: 'veg', price: 3, stock: 20 }, { id: 'seasoning', price: 6, stock: 10 },
    ],
    buys: { veg: 2, commonFish: 4, crucianCarp: 6, carp: 8, egg: 3 },
    services: ['meals'], needs: { cert: 'foodHygiene' }, says: '面婶拿抹布擦了擦桌子，没急着招呼你。',
  },
  pawn: {
    id: 'pawn', name: '当铺', owner: '朝奉先生', scene: 'pawn',
    hours: [9 * 60, 18 * 60], kind: 'pawn',
    sells: [
      { id: 'notePaper', price: 5, stock: 20 }, { id: 'soapNut', price: 4, stock: 10 },
    ],
    buys: { brokenGlass: 1, oldSteeringWheel: 10, villainsEvidence: 30, falsifiedLedger: 25, usedDiff: 20 },
    services: ['pawn'], says: '朝奉先生隔着柜台打量了你的衣裳，慢悠悠放下茶盏。',
  },
  general: {
    id: 'general', name: '杂货铺', owner: '掌柜', scene: 'general',
    hours: [6 * 60, 20 * 60], kind: 'general',
    sells: [
      { id: 'salt', price: 6, stock: 20 }, { id: 'oil', price: 8, stock: 15 },
      { id: 'soapNut', price: 5, stock: 10 }, { id: 'notePaper', price: 4, stock: 20 },
      { id: 'strawSandals', price: 7, stock: 5 }, { id: 'burlapSack', price: 9, stock: 6 },
      { id: 'lamp', price: 25, stock: 3 }, { id: 'seed', price: 3, stock: 30 },
      { id: 'rice_seed', price: 5, stock: 20 },   // 谷种：米的来路，自己插一畦稻
    ],
    buys: { plasticBottle: 1, tornCardboard: 1, tinCan: 1, veg: 2, egg: 3, herb: 2, scrap_wood: 1, paddy: 3 },
    services: [], says: '掌柜的从账本后面抬起头：「要点儿什么？」',
  },
  inn: {
    id: 'inn', name: '大车店', owner: '店家', scene: 'inn',
    hours: null, kind: 'inn',
    sells: [{ id: 'meal', price: 10, stock: 8 }, { id: 'liquor', price: 12, stock: 10 }],
    buys: { firewood: 2, veg: 2, commonFish: 4 },
    services: ['sleepCheap'], needs: { cert: 'residencePermit' },
    says: '店家把算盘一拨：「住店？先说清楚，通铺不包洗脚水。」',
  },
  pharmacy: {
    id: 'pharmacy', name: '药铺', owner: '坐堂郎中', scene: 'pharmacy',
    hours: [8 * 60, 18 * 60], kind: 'pharmacy',
    sells: [
      { id: 'herb', price: 5, stock: 40 }, { id: 'medicine', price: 18, stock: 6 },
      { id: 'soapNut', price: 4, stock: 10 },
    ],
    buys: { herb: 3, insects: 2, commonFish: 4 },
    services: ['treat'], needs: { cert: 'businessTaxClear' }, says: '郎中捏着你的手腕看了片刻。',
  },
  library: {
    id: 'library', name: '书局', owner: '书局掌柜', scene: 'library',
    hours: [8 * 60, 19 * 60], kind: 'library',
    sells: [
      { id: 'notePaper', price: 4, stock: 30 }, { id: 'book', price: 30, stock: 5 },
    ],
    buys: { falsifiedLedger: 20, notePaper: 1 },
    services: ['rent'], needs: { cert: 'studentId' }, says: '掌柜的把书往里挪了挪，怕你手脏。',
  },
  yamen: {
    id: 'yamen', name: '衙门', owner: '知县', scene: 'yamen_hall',
    hours: [8 * 60, 17 * 60], kind: 'yamen',
    sells: [{ id: 'notePaper', price: 6, stock: 20 }],
    buys: { villainsEvidence: 40, falsifiedLedger: 30, smuggledSalt: 5 },
    services: ['licenses'], says: '值堂的差役敲了敲水火棍：「排后面。」',
  },
  pet_shop: {
    id: 'pet_shop', name: '牲口市', owner: '牙子', scene: 'pet_shop',
    hours: [6 * 60, 18 * 60], kind: 'pet_shop',
    sells: [
      { id: 'chicken', price: 30, stock: 4 }, { id: 'petCage', price: 15, stock: 3 },
      { id: 'animalFeed', price: 3, stock: 40 }, { id: 'strawSandals', price: 6, stock: 5 },
    ],
    buys: { egg: 3, wool: 8, chickenManure: 1, insects: 2 },
    services: [], needs: { cert: 'petRegistration' }, says: '牙子拍了拍牲口的背：「这头，牙口好。」',
  },
  depot: {
    id: 'depot', name: '货栈', owner: '账房', scene: 'depot',
    hours: [7 * 60, 19 * 60], kind: 'depot',
    sells: [{ id: 'wovenBag', price: 8, stock: 10 }, { id: 'burlapSack', price: 9, stock: 8 }],
    buys: { smuggledSalt: 12, unstampedLiquor: 15, salt: 5, commonFish: 5, carp: 10, perch: 25, liveCatfish: 18 },
    services: ['quests'], needs: { cert: 'transportLicense' }, says: '账房先生把水牌翻过来：「有货要发？」',
  },
  junkyard: {
    id: 'junkyard', name: '废品收购站', owner: '收破烂的老汉', scene: 'junkyard',
    hours: [6 * 60, 18 * 60], kind: 'junkyard',
    sells: [{ id: 'burlapSack', price: 9, stock: 5 }],
    buys: {
      plasticBottle: 1, tornCardboard: 1, brokenGlass: 1, tinCan: 1, usedClothing: 2,
      scrap_iron: 3, scrap_wood: 2, bolt: 2, constructionDebris: 1, ironRimPiece: 2,
      wasteEngineOil: 4, usedTire: 6, usedValve: 3, burlapSack: 4,
    },
    services: ['bulk'],
    says: '老汉眯着眼把你的货拨了一遍：「就这些？」',
  },
  fun_room: {
    id: 'fun_room', name: '瓦舍', owner: '班主', scene: 'fun_room',
    hours: [16 * 60, 24 * 60], kind: 'fun_room',
    sells: [{ id: 'liquor', price: 15, stock: 20 }, { id: 'meal', price: 12, stock: 10 }],
    buys: { firewood: 2, veg: 2 },
    services: ['gamble'], needs: { cert: 'entertainmentGuard' }, says: '班主一甩袖子：「客官里边请——」',
  },
  onsen: {
    id: 'onsen', name: '汤池', owner: '堂倌', scene: 'onsen',
    hours: [12 * 60, 23 * 60], kind: 'onsen',
    sells: [{ id: 'soapNut', price: 6, stock: 20 }],
    buys: { firewood: 3 },
    services: ['bathe'], needs: { cert: 'nightPass' }, says: '堂倌拎着木桶过来：「水正好。」',
  },
};

/** 有没有到点儿 */
export function isOpen(db, shopId) {
  const shop = SHOPS[shopId];
  if (!shop) return false;
  const w = Repo.getWorld(db);
  if (!shop.hours) return true;
  const [a, b] = shop.hours;
  const m = w.minute || 0;
  return a <= b ? (m >= a && m <= b) : (m >= a || m <= b);
}

export function openHint(shopId) {
  const shop = SHOPS[shopId];
  if (!shop || !shop.hours) return '全天都开着门';
  const hm = (m) => `${Math.floor(m / 60)}时`;
  return `${hm(shop.hours[0])}到${hm(shop.hours[1])}`;
}

/** 买价（你要掏的钱） */
export function buyPrice(db, shopId, itemId) {
  const shop = SHOPS[shopId];
  const row = shop && shop.sells.find((s) => s.id === itemId);
  if (!row) return 0;
  const w = Repo.getWorld(db);
  // 行商那张照 + 身上的气度：会做买卖的人，进货便宜一点
  const raw = row.price * marketFactor(w, shopId, itemId) * Avatar.priceFactor(db) * Inspire.bargain(db, 'buy');
  return Math.max(1, Math.round(raw));
}

/** 卖价（店家给你的钱） */
export function sellPrice(db, shopId, itemId) {
  const shop = SHOPS[shopId];
  if (!shop || shop.buys[itemId] == null) return 0;
  const w = Repo.getWorld(db);
  const raw = shop.buys[itemId] * marketFactor(w, shopId, itemId) * Avatar.priceFactor(db) * Inspire.bargain(db, 'sell');
  return Math.max(0, Math.round(raw));
}

/** 柜台上的菜单，UI 直接拿去画 */
export function shelf(db, shopId) {
  const shop = SHOPS[shopId];
  if (!shop) return [];
  return shop.sells
    .filter((s) => s.price > 0)
    .map((s) => ({
      id: s.id, name: getItem(s.id).name, price: buyPrice(db, shopId, s.id), stock: s.stock,
    }));
}

/** 你身上这家愿意收的货 */
export function sellable(db, shopId) {
  const shop = SHOPS[shopId];
  if (!shop) return [];
  const out = [];
  for (const itemId of Object.keys(shop.buys)) {
    const n = Repo.countItem(db, itemId);
    if (n > 0) out.push({ id: itemId, name: getItem(itemId).name, price: sellPrice(db, shopId, itemId), have: n });
  }
  out.sort((a, b) => a.name.localeCompare(b.name, 'zh'));
  return out;
}

/** 买：扣钱、加货 */
export function buy(db, shopId, itemId, count = 1) {
  const shop = SHOPS[shopId];
  if (!shop) return { ok: false, reason: '没这家铺子。' };
  if (!isOpen(db, shopId)) return { ok: false, reason: '这会儿没开门。' };
  const price = buyPrice(db, shopId, itemId);
  if (!price) return { ok: false, reason: '这里不卖这个。' };
  const p = Repo.getPlayer(db);
  const cost = price * count;
  if ((p.cash || 0) < cost) return { ok: false, reason: `钱不够，要${cost}钱。` };
  Repo.savePlayer(db, { cash: (p.cash || 0) - cost });
  Repo.addItem(db, itemId, count);
  Tags.gain(db, 'trading', 1, 'buy', 12);   // 做买卖这一门，原先没人记过账
  return { ok: true, text: `花了${cost}钱买${getItem(itemId).name}x${count}。` };
}

/** 卖：减货、加钱 */
export function sell(db, shopId, itemId, count = 1) {
  const shop = SHOPS[shopId];
  if (!shop) return { ok: false, reason: '没这家铺子。' };
  if (!isOpen(db, shopId)) return { ok: false, reason: '这会儿没开门。' };
  const price = sellPrice(db, shopId, itemId);
  if (!price) return { ok: false, reason: '这东西不收。' };
  if (!Repo.takeItem(db, itemId, count)) return { ok: false, reason: '身上没有这么多。' };
  const gain = price * count;
  const p = Repo.getPlayer(db);
  Repo.savePlayer(db, { cash: (p.cash || 0) + gain });
  Tags.gain(db, 'trading', 1, 'sell', 12);
  return { ok: true, text: `卖出${getItem(itemId).name}x${count}，得${gain}钱。` };
}

/** 进这家得亮出什么证照（UI 提示用） */
export function missingCerts(db, shopId) {
  const shop = SHOPS[shopId];
  if (!shop || !shop.needs || !shop.needs.cert) return [];
  return [shop.needs.cert];
}

export const Shops = {
  SHOPS, isOpen, openHint, buyPrice, sellPrice, shelf, sellable, buy, sell, missingCerts,
  all: () => Object.values(SHOPS),
};
